<template>
	<div class="info-window" :data-testid="`info-window-${location?.mmsClubId}`">
		<div class="d-flex justify-content-between mb-2">
			<div
				class="h6 mb-0 text-uppercase text-nowrap text-truncate"
				:class="{ 'mb-1': location?.comingSoon }"
			>
				{{ location?.clubTitle }}
			</div>

			<button
				type="button"
				class="btn btn-simple py-0 ps-2"
				:data-testid="`close-window-${location?.mmsClubId}`"
				@click="closeWindow"
			>
				<CloseIcon :width="12" :height="12" />
			</button>
		</div>
		<div v-if="location?.comingSoon" class="my-1">
			<span class="badge rounded-pill rounded-pill-sm border">{{ location.comingSoon }}</span>
		</div>

		<div v-if="location?.locationMessage" class="my-1">
			<span class="badge rounded-pill rounded-pill-sm border">{{ location.locationMessage }}</span>
		</div>

		<div class="mb-2">
			{{ location?.street1 }}<br />
			{{ location?.city }}, {{ location?.stateAbbr }}
			{{ location?.zip }}
		</div>

		<div class="d-flex justify-content-between align-items-center">
			<div>
				<a
					v-if="phoneHref"
					:id="phoneId"
					class="phone"
					style="text-underline-offset: unset"
					:href="phoneHref"
					data-testid="phone-link"
					@click.prevent="trackPhone(phoneHref, interactions)"
				>
					{{ displayPhone }}
				</a>
			</div>
			<div>
				<a
					v-if="location?.prospectPagePath"
					:id="detailsId"
					:href="location.prospectPagePath"
					class="details btn btn-simple btn-icon focus-ring"
					data-testid="details-link"
					@click.prevent="trackDetails(location.prospectPagePath, interactions)"
				>
					<ArrowRightParensIcon :width="16" :height="16" /> Details
				</a>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import ArrowRightParensIcon from 'Lib/icons/ArrowRightParensIcon.vue';
import { useAdobeDataLayer } from '@/composables/use-adobe-data-layers';
import { useD360MapFiltersStore } from '@/stores/map-d360-filters';
import CloseIcon from 'Lib/icons/CloseIcon.vue';
import type { Location } from 'Types/aemui/find-a-location/findaLocation';

const props = defineProps<{
	location: Location | null;
}>();

const emit = defineEmits(['closeWindow']);

const closeWindow = () => {
	emit('closeWindow');
};

const d360MapStore = useD360MapFiltersStore();
const { selectedFilters } = storeToRefs(d360MapStore);

const interactions = computed(() => {
	const selected = selectedFilters.value.map((filter) => filter.name);
	return {
		action: 'filter-applied',
		filters: { selected }
	};
});

const displayPhone = computed(() => props.location?.phoneClubWaitlist ?? props.location?.phone);

const phoneHref = computed(() =>
	props.location?.phoneClubWaitlist
		? `tel://${props.location?.phoneClubWaitlist}`
		: `tel://${props.location?.phoneLink}`
);

const { componentId: detailsId, trackLink: trackDetails } = useAdobeDataLayer({
	name: 'findlocation',
	type: 'link',
	title: 'Details (map)',
	link: props.location?.prospectPagePath
});

const { componentId: phoneId, trackLink: trackPhone } = useAdobeDataLayer({
	name: 'findlocation',
	type: 'link',
	title: displayPhone.value ?? '',
	link: phoneHref.value
});

defineExpose({
	trackDetails,
	trackPhone
});
</script>

<style scoped lang="scss">
.info-window {
	width: 164px;

	@include media-breakpoint-up(sm) {
		width: 200px;
	}
	@include media-breakpoint-up(md) {
		width: 280px;
	}
	@include media-breakpoint-up(lg) {
		width: 200px; // map is half-screen here so needs narrower info window
	}
	@include media-breakpoint-up(xl) {
		width: 280px;
	}

	.badge.rounded-pill {
		font-size: 0.875em;
	}

	.phone {
		font-size: 0.75rem;
	}

	.details {
		font-size: 0.75rem;
	}
}
</style>
